import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../../state/worldStore';
import { useBodyStore, mannequinRig } from '../../game/mannequins';
import AnimatedResident from '../AnimatedResident';
import SafeMount from '../SafeMount';
import { useHeliTour, usePilots, pilotFor, padById } from '../../city/heliTour';
import { baseGroundY } from '../../city/cityGround';
import { useHeliKit } from './Helicopter';
import { CABIN_SEATS, PILOT_SEAT } from './HeliDirector';
import { seatedIdle, chatTalkMove, emoteMove } from '../../city/bodyLanguage';
import { useCityStore } from '../../city/cityStore';
import { stateAt } from '../../city/cityMotion';
import { MOMENT_ID, useMoment } from '../../city/moment';
import { serverNow, cityEngage, cityRelease } from '../../services/worldSocket';
import { bodyScene, useSceneChat } from '../SceneChat';

/* ── TALK TO THE PILOT AT THE PAD (2026-11-04) ───────────────────────────────
   Khari: "we have added a new cast member named jordan. she flies the
   helicopters … Users should be able to speak to her while she is standing at
   the entrance to the ride. I will make the other pilot cast members now, and
   they should have the same abilities."
   Every pad's pilot (whoever /admin/cast puts at `heliport:pad-N`), while they
   stand at their spot by the pad: a tap opens the chat as a scene on their body
   (SceneChat bodyScene), typed or by voice, the same one-to-one chat as any
   artist in the world. Their own brain knows the heliport, their pad and whether
   their helicopter is waiting, boarding or up (API services/city/heliPilot).
   While they stand there they move like the rest of the town: a move asked of
   them (API city/moveBook, `city_emote`), and their hands while their reply to
   you is spoken. Once passengers board they are off to the helicopter; the
   API refuses a move then and tells them why (hands on the controls). */
function usePilotStanding(avatarId, standing) {
  const [move, setMove] = useState(null);
  useEffect(() => {
    if (!standing || !avatarId) { setMove(null); return undefined; }
    const tick = () => {
      const t = serverNow();
      const ws = useWorldStore.getState();
      const withYou = String(ws.chatAvatar?.avatarId ?? '') === String(avatarId);
      const toYou = withYou && (ws.residentTalking || useSceneChat.getState().speaking === Number(avatarId));
      // …or saying a line to an artist who came by (API city/cityHome: they live here now; city/cityStore `speaking`).
      const cs = useCityStore.getState();
      const toThem = (cs.speaking?.[avatarId] || 0) > t;
      const next = emoteMove(cs.emotes, avatarId, t) || (toYou || toThem ? chatTalkMove(avatarId, t, 50) : null);
      setMove((cur) => (cur === next ? cur : next));
    };
    tick();
    const iv = setInterval(tick, 300);
    return () => clearInterval(iv);
  }, [avatarId, standing]);
  return move;
}

/** Which way to face, in the group's parent space: toward the camera while you talk with them (the chat
 *  scene's camera comes in from your side, as with every resident), else their spot's own facing. */
const _cam = new THREE.Vector3();
function faceYaw(g, camera, avatarId, restYaw) {
  if (String(useWorldStore.getState().chatAvatar?.avatarId ?? '') !== String(avatarId) || !g.parent) return restYaw;
  g.parent.worldToLocal(_cam.copy(camera.position));
  return Math.atan2(_cam.x - g.position.x, _cam.z - g.position.z);
}

/** Tap → their chat, as a scene on the group their body stands in. Only while they stand at the pad. */
function usePilotTap(grp, padId, avatarId, height, standing) {
  const name = usePilots((s) => pilotFor(padId, s)?.name || null);
  /* HELD FOR YOU WHILE YOU TALK (2026-11-05). A pilot lives in the city now (API
     city/cityHome), so an artist could run into them mid-chat. The tap holds them
     the way a tap on any resident does (city_engage: nobody starts a conversation
     with someone held), and the hold is let go when the chat closes. The chat
     itself stays the pilot's own (no city surface): it opens whatever the hold says. */
  const held = useRef(false);
  useEffect(() => {
    if (!avatarId) return undefined;
    const off = useWorldStore.subscribe((st) => {
      if (held.current && Number(st.chatAvatar?.avatarId) !== Number(avatarId)) { held.current = false; cityRelease(avatarId); }
    });
    return () => { off(); if (held.current) { held.current = false; cityRelease(avatarId); } };
  }, [avatarId]);
  return useCallback(() => {
    if (!standing || !avatarId || !grp.current) return;
    useWorldStore.getState().openChat({ avatarId, name: name || 'Pilot', scene: bodyScene(grp.current, { avatarId, height }) });
    held.current = true;   // after the chat is theirs, so the release above never fires on the way in
    cityEngage(avatarId).catch?.(() => {});
  }, [grp, avatarId, name, height, standing]);
}

// THE PEOPLE IN A HELICOPTER (2026-11-01). Khari: "I'd like to be able to see
// the passengers I am riding with. they are currently invisible during the
// ride."
//
// Whoever is aboard a pad's helicopter (the server's `seats`, in boarding order)
// sits in it — in the same seat HeliDirector puts their camera in
// (CABIN_SEATS), in their own body: a player's chosen mannequin (the one others
// see them walk in, game/mannequins), strapped in (poses.js `passenger`). The pad's
// pilot (city/heliTour usePilots — a cast member, once their body is built) flies
// it from the front right seat: see PilotCrew.
//
// Drawn INSIDE the helicopter's posed group (Helicopter `children`), in its kit
// space, so they bank and turn with it — and so everyone in the street sees who
// is flying, not an empty helicopter. Their bodies on the ground are hidden
// while they are aboard (Peers, PlayerBody). Yours is left out only when you
// are looking out through your own eyes (the window seat, in the air).
const HIP_BACK = 0.06;       // the hips sit a hand forward of the seat back (the seat's camera z is the eye)
const SEAT_HIPS = 0.72;      // kit: hips on a cabin cushion (its top is ≈0.69), as the pilot's (CUSHION_HIPS)

/* ⚠ A RECORDED SEATED IDLE, NOT A POSE (2026-11-04). Khari: "seated characters in
   helicopters should use a seated animation. user included. As of now, they seem
   to sit in a t-pose." The procedural `passenger` pose wrote its offsets onto each
   rig's own bind pose, so a body bound with its arms out sat with them out. Now
   the library's seated idle, fitted to each body (city/bodyLanguage seatedIdle,
   by how the body presents), its hips put on the cushion (AnimatedResident `seatHips`). */
function SeatedBody({ rig, seat, avatarId = null }) {
  return (
    <group position={[seat.x, 0, seat.z - HIP_BACK]}>
      <SafeMount key={rig.rigged} label={`heli passenger ${rig.key || avatarId || ''}`} retryMs={30000}>
        <Suspense fallback={null}>
          <AnimatedResident
            meshUrl={rig.rigged} rigPose={rig.pose || null} clipMap={rig.clips || null}
            activity={seatedIdle(rig.presents, avatarId ?? 0)} seatHips={SEAT_HIPS}
            avatarId={avatarId} position={[0, 0, 0]} rotationY={0} height={rig.height || 0.85}
          />
        </Suspense>
      </SafeMount>
    </group>
  );
}

/* ── THE PILOT WALKS OUT TO HER HELICOPTER AND SITS DOWN TO FLY IT (2026-11-03) ──
   Khari: "the helicopter pilot was made with a sitting pose as well as a walk to
   sit animation. please add these to her so she can walk to the helicopter and
   sit while flying it."

   One body, in the helicopter's kit space the whole time (it stands parked on the
   pad, so its space IS the pad's):
     stand    at her spot by the pad (data/heli_routes.json pads[].pilot), idle
     walk     passengers are boarding: her walk, from the spot to the pilot's door
     enter    in through it, up onto the seat, turning to face the front
     sit      the end of her own `walk_to_sit` — from the moment it faces front
              again (SIT_FROM) to its last frame, once, held
     seated   her own `chair_sit_idle_f`, for the flight
     out/back landed and emptied: out of the door, and walked back to her spot
   The two seated clips are re-rooted (AnimatedResident `rootAt`): the sit-down's
   LAST frame and the seated idle's AVERAGE both put the hips over the group's
   origin, which is the seat, so the hand-over between them does not jump.
   Take-off pressed before she is in: she hurries (a long way out → in at the
   door at once). A body without these clips flies the old way (SeatedBody,
   standing at the spot while the pad is idle). Clip lengths and the seated hip
   height are measured off her own files (2026-11-03): walk_to_sit 8.37 s, facing
   front again by ~5.9 s; seated hips at 0.40 of her height. */
const WALK_SPEED = 0.6;       // world units a second: an easy walk for a ~0.9-unit body
const ENTER_S = 0.9;
const SIT_FROM = 5.9;
const SIT_S = 2.4;
const CUSHION_HIPS = 0.72;    // kit: hips on the pilot's cushion (the cushion top is ≈0.69)
const HIPS_SEATED = 0.40;     // seated hip height ÷ body height
const ROOT_AT = { walk_to_sit: 'end', chair_sit: 'mean' };
const wrapA = (a) => Math.atan2(Math.sin(a), Math.cos(a));

function PilotCrew({ padId, state, body, avatarId }) {
  const kit = useHeliKit();
  const height = Number(body.height) || 0.75;
  const sitKey = Object.keys(body.clips || {}).find((k) => /^chair_sit/.test(k));
  const urls = useMemo(() => [body.walk, body.idle, body.clips?.walk_to_sit, sitKey ? body.clips[sitKey] : null].filter(Boolean),
    [body.walk, body.idle, body.clips, sitKey]);
  // The marks, in the parked helicopter's kit space.
  const marks = useMemo(() => {
    const pad = padById(padId);
    const sp = pad?.pilot;
    const c = Math.cos(pad?.rotY || 0); const s = Math.sin(pad?.rotY || 0);
    const dx = (sp?.x ?? pad?.x ?? 0) - (pad?.x ?? 0); const dz = (sp?.z ?? pad?.z ?? 0) - (pad?.z ?? 0);
    const sy = sp ? baseGroundY(sp.x, sp.z) - baseGroundY(pad.x, pad.z) : 0;
    const seatZ = PILOT_SEAT.z - HIP_BACK;
    return {
      spot: new THREE.Vector3(dx * c - dz * s, sy, dx * s + dz * c),
      spotYaw: wrapA((sp?.rotY || 0) - (pad?.rotY || 0)),
      door: new THREE.Vector3(-(kit.size.x / 2 + 0.3), 0, seatZ),
      seat: new THREE.Vector3(PILOT_SEAT.x, CUSHION_HIPS - HIPS_SEATED * height, seatZ),
    };
  }, [padId, kit, height]);

  const grp = useRef(null);
  const run = useRef({ phase: 'stand', t: 0, from: new THREE.Vector3(), fromYaw: 0, fast: false });
  const [clip, setClip] = useState({ play: 'idle', once: false, from: null });
  const [atSpot, setAtSpot] = useState(true);
  const go = (phase, c) => {
    const g = grp.current;
    run.current = { ...run.current, phase, t: 0, from: g ? g.position.clone() : marks.spot.clone(), fromYaw: g ? g.rotation.y : marks.spotYaw };
    if (c) setClip(c);
    setAtSpot(phase === 'stand');
  };
  const standing = atSpot && state === 'idle';
  const standMove = usePilotStanding(avatarId, standing);
  const onTap = usePilotTap(grp, padId, avatarId, height, standing);
  const camera = useThree((st) => st.camera);
  const turnTo = (g, want, dt) => { g.rotation.y += wrapA(want - g.rotation.y) * Math.min(1, dt * 7); };
  const walkTo = (g, to, dt, speed) => {
    const d = new THREE.Vector3().subVectors(to, g.position); d.y = 0;
    const L = d.length();
    if (L < 0.03) { g.position.x = to.x; g.position.z = to.z; return true; }
    turnTo(g, Math.atan2(d.x, d.z), dt);
    g.position.addScaledVector(d.normalize(), Math.min(L, speed * dt));
    g.position.y = THREE.MathUtils.lerp(g.position.y, to.y, Math.min(1, dt * 4));
    return false;
  };

  useFrame((_, dt) => {
    const g = grp.current; if (!g) return;
    const r = run.current; r.t += dt;
    const busy = state !== 'idle';
    const flying = state === 'flying';
    switch (r.phase) {
      case 'stand':
        g.position.copy(marks.spot);
        turnTo(g, faceYaw(g, camera, avatarId, marks.spotYaw), dt);   // to you while you talk with them
        if (busy) go('walk', { play: 'walk', once: false, from: null });
        break;
      case 'walk': {
        if (!busy) { go('back', null); break; }
        if (flying && !r.fast) {
          r.fast = true;
          // Too far to make it in time: in at the door at once.
          if (g.position.distanceTo(marks.door) > 1.2) { g.position.copy(marks.door); go('enter', null); break; }
        }
        if (walkTo(g, marks.door, dt, WALK_SPEED * (r.fast ? 2.5 : 1))) go('enter', null);
        break;
      }
      case 'enter': {
        if (!busy) { go('out', null); break; }
        const k = Math.min(1, r.t / (r.fast ? ENTER_S / 2 : ENTER_S));
        const e = k * k * (3 - 2 * k);
        g.position.lerpVectors(r.from, marks.seat, e);
        g.rotation.y = r.fromYaw + wrapA(0 - r.fromYaw) * e;     // round to face the nose (kit +z)
        if (k >= 1) go('sit', { play: 'walk_to_sit', once: true, from: SIT_FROM });
        break;
      }
      case 'sit':
        g.position.copy(marks.seat); g.rotation.y = 0;
        if (!busy) { go('out', null); break; }
        if (r.t >= SIT_S) go('seated', { play: 'chair_sit', once: false, from: null });
        break;
      case 'seated':
        g.position.copy(marks.seat); g.rotation.y = 0;
        if (!busy) go('out', null);
        break;
      case 'out':
        // Out of the door, standing, and off back to her spot.
        g.position.copy(marks.door); g.rotation.y = Math.atan2(marks.spot.x - marks.door.x, marks.spot.z - marks.door.z);
        r.fast = false;
        go('back', { play: 'walk', once: false, from: null });
        break;
      case 'back':
        if (busy) { r.fast = false; go('walk', { play: 'walk', once: false, from: null }); break; }
        if (walkTo(g, marks.spot, dt, WALK_SPEED)) { r.fast = false; go('stand', { play: 'idle', once: false, from: null }); }
        break;
      default: go('stand', { play: 'idle', once: false, from: null });
    }
  });

  return (
    <group ref={grp} position={marks.spot.toArray()} rotation={[0, marks.spotYaw, 0]}>
      <SafeMount key={body.rigged} label={`heli pilot ${avatarId}`} retryMs={30000}>
        <Suspense fallback={null}>
          <AnimatedResident
            meshUrl={body.rigged} rigPose={body.pose || null} clipMap={body.clips || null} clipUrls={urls}
            play={clip.play} playOnce={clip.once} playFrom={clip.from} rootAt={ROOT_AT}
            activity={standing ? standMove : null} activityTalks={!!standMove}
            onSelect={standing ? onTap : null}
            avatarId={avatarId} position={[0, 0, 0]} rotationY={0} height={height}
          />
        </Suspense>
      </SafeMount>
    </group>
  );
}

/** A pilot with no walk-to-sit clips: standing at the spot while idle, strapped in (pose) while busy. */
function PilotSimple({ padId, state, body, avatarId }) {
  const pad = padById(padId);
  const sp = pad?.pilot;
  const grp = useRef(null);
  const height = Number(body.height) || 0.75;
  const standing = state === 'idle';
  const standMove = usePilotStanding(avatarId, standing);
  const onTap = usePilotTap(grp, padId, avatarId, height, standing);
  const camera = useThree((st) => st.camera);
  const spot = useMemo(() => {
    if (!sp || !pad) return null;
    const c = Math.cos(pad.rotY || 0); const s = Math.sin(pad.rotY || 0);
    const dx = sp.x - pad.x; const dz = sp.z - pad.z;
    return { pos: [dx * c - dz * s, baseGroundY(sp.x, sp.z) - baseGroundY(pad.x, pad.z), dx * s + dz * c], yaw: wrapA((sp.rotY || 0) - (pad.rotY || 0)) };
  }, [sp, pad]);
  useFrame((_, dt) => {
    const g = grp.current; if (!g || !spot || !standing) return;
    const want = faceYaw(g, camera, avatarId, spot.yaw);
    g.rotation.y += wrapA(want - g.rotation.y) * Math.min(1, dt * 7);
  });
  if (state !== 'idle') return <SeatedBody rig={{ rigged: body.rigged, pose: body.pose, clips: body.clips, height: Number(body.height) || 0.75, presents: body.presents || null }} seat={PILOT_SEAT} avatarId={avatarId} />;
  if (!spot) return null;
  return (
    <group ref={grp} position={spot.pos} rotation={[0, spot.yaw, 0]}>
      <SafeMount key={body.rigged} label={`heli pilot ${avatarId}`} retryMs={30000}>
        <Suspense fallback={null}>
          <AnimatedResident meshUrl={body.rigged} rigPose={body.pose || null} clipMap={body.clips || null}
            clipUrls={[body.walk, body.idle].filter(Boolean)} play="idle" avatarId={avatarId} position={[0, 0, 0]} rotationY={0} height={height}
            activity={standMove} activityTalks={!!standMove} onSelect={onTap} />
        </Suspense>
      </SafeMount>
    </group>
  );
}

/* AT WORK, OR NOT IN THE GAME (2026-11-05). Khari: "if we change that, then the
   pilots will not show when the venue is closed … if the cast is not at their
   location, they are unavailable and out of the game." A pilot lives in the city
   (API city/cityHome): while their day says away (the heliport closed) nobody
   stands by the pad. With no live city, or a pilot who is not a resident, they
   stand there as before. */
function useOnDuty(avatarId) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    if (!avatarId) return undefined;
    const tick = () => {
      const cs = useCityStore.getState();
      const r = cs.live ? cs.byId?.[avatarId] : null;
      const next = r ? !!stateAt(r, serverNow(), cs.convs) : true;
      setOn((cur) => (cur === next ? cur : next));
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => clearInterval(iv);
  }, [avatarId]);
  return on;
}

export default function HeliPassengers({ padId }) {
  const pad = useHeliTour((s) => s.pads[padId]);
  const myId = useHeliTour((s) => s.myId);
  const offline = useHeliTour((s) => s.offline);
  const view = useHeliTour((s) => s.view);
  const mine = useHeliTour((s) => s.mine?.pad === padId);
  const presences = useWorldStore((s) => s.presences);
  const npcList = useWorldStore((s) => s.npcList);
  const myBody = useBodyStore((s) => s.body);
  const pilotId = usePilots((s) => pilotFor(padId, s)?.avatarId || null);
  const onDuty = useOnDuty(pilotId);
  // A MOMENT'S STILL of this pilot (city/moment, a flight's: API HeliRideService): the moment stands
  // them on their mark itself, so the pad does not draw them twice. Other pilots stand as ever.
  const inMoment = useMoment((s) => !!(MOMENT_ID && pilotId && s.moment && [Number(s.moment.a?.id), Number(s.moment.b?.id)].includes(Number(pilotId))));
  const pilot = useMemo(
    () => (pilotId ? (npcList || []).find((n) => Number(n?.avatarId) === pilotId && n.rigged && !n.test) : null) || null,
    [npcList, pilotId],
  );

  const state = pad?.state || 'idle';
  const aboard = state !== 'idle' ? (pad?.seats || []) : [];
  const looking = mine && view === 'cabin' && state === 'flying';
  const bodies = aboard.slice(0, CABIN_SEATS.length).map((s, i) => {
    // AN ARTIST GOING UP (API city/HeliRideService, 2026-11-05): their own body in the seat, like the pilot's.
    if (s?.avatarId) {
      const b = (npcList || []).find((n) => Number(n?.avatarId) === Number(s.avatarId) && n.rigged && !n.test);
      return b ? <SeatedBody key={s.id} rig={{ rigged: b.rigged, pose: b.pose, clips: b.clips, height: Number(b.height) || 0.75, presents: b.presents || null }} seat={CABIN_SEATS[i]} avatarId={Number(s.avatarId)} /> : null;
    }
    const me = offline ? i === 0 : s?.id && s.id === myId;
    if (me && looking) return null;                 // you are the camera
    const key = me ? myBody : (presences || []).find((p) => p.id === s?.id)?.body;
    const rig = mannequinRig(key);
    return rig ? <SeatedBody key={s?.id || i} rig={rig} seat={CABIN_SEATS[i]} /> : null;
  });
  const sits = !!(pilot?.clips?.walk_to_sit && Object.keys(pilot.clips).some((k) => /^chair_sit/.test(k)) && pilot.walk);
  return (
    <>
      {bodies}
      {pilot && !inMoment && (onDuty || state !== 'idle') && (sits
        ? <PilotCrew key={`crew:${pilotId}`} padId={padId} state={state} body={pilot} avatarId={pilotId} />
        : <PilotSimple key={`simple:${pilotId}`} padId={padId} state={state} body={pilot} avatarId={pilotId} />)}
    </>
  );
}
